import DOMPurify from 'dompurify';

export const FOLDER_LABEL = {
  inbox: { id: 'Kotak Masuk', en: 'Inbox' }, sent: { id: 'Terkirim', en: 'Sent' }, draft: { id: 'Draft', en: 'Drafts' },
  archive: { id: 'Arsip', en: 'Archive' }, spam: { id: 'Spam', en: 'Spam' }, trash: { id: 'Sampah', en: 'Trash' }, snoozed: { id: 'Ditunda', en: 'Snoozed' },
};
export const folderLabel = (f, lang) => (f.type === 'system' ? FOLDER_LABEL[f.name]?.[lang] || f.name : f.name);

export const fmtBytes = (n) => {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

export const fmtTime = (iso, lang) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const locale = lang === 'id' ? 'id-ID' : 'en-US';
  if (sameDay) return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  if (d.getFullYear() === now.getFullYear()) return d.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  return d.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
};
export const fmtFull = (iso, lang) => (iso ? new Date(iso).toLocaleString(lang === 'id' ? 'id-ID' : 'en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '');

export const senderName = (m) => m.from_name || m.from_address || '';
export const initials = (s = '') => s.replace(/[<>"]/g, '').trim().split(/[\s@._-]+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';

/** Sanitize incoming HTML; remote images are blocked until `showImages`. */
export const safeHtml = (html, showImages = false) => {
  if (!html) return '';
  let out = DOMPurify.sanitize(html, { FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'textarea', 'select', 'button'], FORBID_ATTR: ['onerror', 'onload'], ADD_ATTR: ['target'] });
  out = out.replace(/<a\s/gi, '<a target="_blank" rel="noopener noreferrer" ');
  if (!showImages) out = out.replace(/(<img[^>]*?)\ssrc=("|')(https?:\/\/[^"']*)\2/gi, '$1 data-blocked-src=$2$3$2');
  return out;
};
export const hasRemoteImages = (html = '') => /<img[^>]*\ssrc=("|')https?:\/\//i.test(html);

export const quoteHtml = (m, lang) => {
  const id = lang === 'id';
  const head = `${id ? 'Pada' : 'On'} ${fmtFull(m.received_at, lang)}, ${m.from_name ? `${m.from_name} &lt;${m.from_address}&gt;` : m.from_address} ${id ? 'menulis' : 'wrote'}:`;
  const body = m.body_html || `<p>${(m.body_text || '').replace(/\n/g, '<br/>')}</p>`;
  return `<br/><br/><div class="pillot-quote" style="color:#6b7280;font-size:13px">${head}</div><blockquote style="margin:6px 0 0;padding-left:12px;border-left:3px solid #d1d5db;color:#4b5563">${DOMPurify.sanitize(body)}</blockquote>`;
};

export const forwardHtml = (m, lang) => {
  const id = lang === 'id';
  const row = (k, v) => (v ? `<div><b>${k}:</b> ${v}</div>` : '');
  const header = `<div style="color:#6b7280;font-size:13px;margin-bottom:8px">---------- ${id ? 'Pesan diteruskan' : 'Forwarded message'} ----------<br/>` +
    row(id ? 'Dari' : 'From', m.from_name ? `${m.from_name} &lt;${m.from_address}&gt;` : m.from_address) + row(id ? 'Tanggal' : 'Date', fmtFull(m.received_at, lang)) +
    row(id ? 'Subjek' : 'Subject', m.subject) + row(id ? 'Kepada' : 'To', (m.to_addresses || []).join(', ')) + '</div>';
  return `<br/><br/>${header}${DOMPurify.sanitize(m.body_html || `<p>${(m.body_text || '').replace(/\n/g, '<br/>')}</p>`)}`;
};

export const replySubject = (s = '') => (/^re:/i.test(s) ? s : `Re: ${s}`);
export const fwdSubject = (s = '') => (/^fwd?:/i.test(s) ? s : `Fwd: ${s}`);
export const splitAddrs = (s = '') => s.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);

export const SNOOZE_PRESETS = (lang) => {
  const id = lang === 'id';
  const d = (h, setHour) => { const x = new Date(); x.setHours(x.getHours() + h); if (setHour != null) x.setHours(setHour, 0, 0, 0); return x; };
  const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1); tomorrow.setHours(8, 0, 0, 0);
  const nextWeek = new Date(); nextWeek.setDate(nextWeek.getDate() + 7); nextWeek.setHours(8, 0, 0, 0);
  const later = new Date(); later.setHours(later.getHours() + 3);
  return [[id ? '3 jam lagi' : 'In 3 hours', later], [id ? 'Besok 08.00' : 'Tomorrow 8 AM', tomorrow], [id ? 'Minggu depan' : 'Next week', nextWeek]];
};
export const toLocalInput = (d) => { const x = new Date(d.getTime() - d.getTimezoneOffset() * 60000); return x.toISOString().slice(0, 16); };

export const DELIVERY_LABEL = {
  scheduled: { id: 'Terjadwal', en: 'Scheduled', cls: 'bg-sky-100 text-sky-800' },
  sent: { id: 'Terkirim ke server', en: 'Sent', cls: 'bg-muted text-muted-foreground' },
  delivered: { id: 'Tersampaikan', en: 'Delivered', cls: 'bg-emerald-100 text-emerald-800' },
  delivery_delayed: { id: 'Tertunda', en: 'Delayed', cls: 'bg-amber-100 text-amber-800' },
  bounced: { id: 'Gagal (bounce)', en: 'Bounced', cls: 'bg-rose-100 text-rose-800' },
  complained: { id: 'Dilaporkan spam', en: 'Complained', cls: 'bg-rose-100 text-rose-800' },
  failed: { id: 'Gagal', en: 'Failed', cls: 'bg-rose-100 text-rose-800' },
  canceled: { id: 'Dibatalkan', en: 'Canceled', cls: 'bg-muted text-muted-foreground' },
};
export const FLAG_LABEL = {
  lookalike_domain: { id: 'Domain pengirim mirip domain perusahaan Anda', en: 'Sender domain looks like your company domain' },
  display_name_spoof: { id: 'Nama tampilan berisi alamat email yang berbeda dari pengirim asli', en: 'Display name contains an email address different from the real sender' },
  reply_to_mismatch: { id: 'Alamat balasan (Reply-To) berbeda dari pengirim', en: 'Reply-To address differs from sender' },
  suspicious_links: { id: 'Tautan mencurigakan (teks tautan ≠ tujuan, IP, punycode, atau pemendek URL)', en: 'Suspicious links (text ≠ target, IP address, punycode or shortener)' },
  urgent_credentials: { id: 'Bahasa mendesak meminta verifikasi akun/kata sandi', en: 'Urgent language asking to verify account/password' },
};
export const DARK_KEY = 'pillot.webmail.dark';
export const SHORTCUTS = [['c', 'Tulis email', 'Compose'], ['/', 'Fokus pencarian', 'Focus search'], ['j / k', 'Pesan berikutnya / sebelumnya', 'Next / previous message'], ['Enter', 'Buka pesan', 'Open message'], ['r', 'Balas', 'Reply'], ['a', 'Balas semua', 'Reply all'], ['f', 'Teruskan', 'Forward'], ['e', 'Arsipkan', 'Archive'], ['s', 'Bintang', 'Star'], ['u', 'Tandai belum dibaca', 'Mark unread'], ['#', 'Hapus', 'Delete'], ['Esc', 'Tutup / kembali', 'Close / back'], ['?', 'Daftar pintasan', 'Shortcut list']];
